// src/components/pages/benefaction/Request/DonationRequestBase.tsx
// ----------------------------------------------------------------------------------
// Change summary (design-only):
// • Use the shared DGA container width by wrapping page/empty states with `dga-container`
//   (same left/right gap as the "طلب تبرع" screen).
// • No logic changes. All behaviors, state, and child components are untouched.
// • ✅ Added ONE new nav tab: "عرض كل الطلبات" that navigates to /benefaction/requests
// ----------------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "../../../../utils/i18nStub";

import {
  createDonationRequest,
  type DonationRequestCreated,
  listDonationRequests,
  type DonationRequestItem,
} from "../../../../services/donationRequestService";

import * as donationsService from "../../../../services/donationsService";
import * as donorService from "../../../../services/donorService";
import { API_BASE_URL } from "../../../../config";
import { getAuthToken, isAuthenticated } from "../../../../services/authService";

// Panels
import LandInfoPanel from "./LandInfoPanel";
import ApplicantPanel from "./ApplicantPanel";
import RequestsSection from "./RequestsSection";

// Declaration modal now from the SAME folder
import Breadcrumbs from "../../../../components/navigation/Breadcrumbs";
import NotBenefactorModal from "../../../NotBenefactorModal";
import { labelForLandRequestStage } from "../../../../utils/donationRequestUiStages";
import { buildLandInfoRows, type LandInfoRows } from "../../../../utils/landInfoRows";
import LandDonationJourneyStepper from "./LandDonationJourneyStepper";

/* ----------------------------- Types & consts ----------------------------- */

type LandDetail = {
  id: number;
  code?: string;
  landCode?: string;
  sequence?: string;
  landNumber?: string | number;
  latitude?: number;
  longitude?: number;
  fullAddress?: string;
  regionName?: string;
  cityName?: string;
  centerName?: string;
  /** بعض الـ APIs تستخدم اسم المركز بهذا الحقل */
  moiaCenterName?: string;
  districtName?: string;
  aproxLandPrice?: number;
  approxLandPrice?: number;
  aprox_land_price?: number;
  donationTypeId?: number;
  /** من الـ API عند توفرها */
  donationType?: string;
  donationTypeName?: string;
};

type DonorRow = {
  donorIdToken: string;
  name: string;
  nationalId?: string;
  phone?: string;
  email?: string;
  donorType?: string; // "direct" | "indirect"
};

export type RequestFileRow = {
  id: number;
  name: string;
  documentTypeId: number; // 1=Bank Letter, 2=POA
  attachmentId: number;
  accessToken: string;
  tokenDonorId: string;
};

const DOC = { FinancialCapacity: 1, PowerOfAttorney: 2 } as const;
const ACCEPT_MIME = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

const numberSar = (v: number | undefined | null) =>
  typeof v === "number" && !isNaN(v) ? v.toLocaleString("en-US") : "-";

type SectionId = "land" | "applicant" | "requests" | "send";
type SectionNavItem = { id: SectionId; label: string };

const landSelectedDonorStorageKey = (id: number) => `benefaction:landDonor:${id}`;

function SectionNavGlyph({ id, className }: { id: SectionId; className?: string }) {
  const cn = `h-5 w-5 shrink-0 ${className ?? ""}`;
  switch (id) {
    case "land":
      return (
        <svg className={cn} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case "applicant":
      return (
        <svg className={cn} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    case "requests":
      return (
        <svg className={cn} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    case "send":
      return (
        <svg
          className={`${cn} -rotate-90`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      );
    default:
      return null;
  }
}

/* -------------------------------- Component ------------------------------- */

export default function DonationRequestBase() {
  const { t } = useTranslation(["BenefactionRequest", "Docs"]);
  // Removed i18n usage - hardcoded for Arabic
  const dir = "rtl";
  const { landId: landIdParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reqTokenFromUrl = (searchParams.get("req") || "").trim();
  const donorTokenFromUrl = (searchParams.get("donor") || "").trim();
  const landId = useMemo(() => {
    const num = Number(landIdParam);
    return Number.isFinite(num) && num > 0 ? num : 0;
  }, [landIdParam]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  const [land, setLand] = useState<LandDetail | null>(null);
  const [donors, setDonors] = useState<DonorRow[]>([]);
  const [selectedDonorToken, setSelectedDonorToken] = useState<string>("");

  const [created, setCreated] = useState<DonationRequestCreated | null>(null);

  // Requests
  const [requests, setRequests] = useState<DonationRequestItem[]>([]);
  const [selectedReq, setSelectedReq] = useState<DonationRequestItem | null>(null);
  const [loadingRequests, setLoadingRequests] = useState(false);

  // Files & edit
  const [files, setFiles] = useState<RequestFileRow[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [previews, setPreviews] = useState<Record<number, string>>({});
  const [docType, setDocType] = useState<number>(DOC.FinancialCapacity);
  const uploadBusy = false;
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  // Pending picks (local only) — user can تعديل before submit
  const [pendingFiles, setPendingFiles] = useState<Record<number, File | null>>({
    [DOC.FinancialCapacity]: null,
    [DOC.PowerOfAttorney]: null,
  });
  const MAX_MB = 10;
  const MAX_BYTES = MAX_MB * 1024 * 1024;

  // Submit (finalize)
  const [submittingReq, setSubmittingReq] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState<string | null>(null);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [showNotBenefactorModal, setShowNotBenefactorModal] = useState(false);

  const [activeSection, setActiveSection] = useState<SectionId>("land");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  /** طي شريط أقسام الطلب على الشاشات الواسعة (xl) — افتراضياً مطوي */
  const [sectionSidebarCollapsed, setSectionSidebarCollapsed] = useState(true);
  const sectionNav = useMemo<SectionNavItem[]>(
    () => [
      { id: "land", label: t("nav.landInfo", "بيانات الأرض") },
      { id: "applicant", label: t("nav.applicantInfo", "بيانات مقدم الطلب") },
      { id: "requests", label: t("nav.requests", "المستندات والمرفقات") },
      { id: "send", label: t("nav.send", "إرسال الطلب") },
    ],
    [t]
  );
  const sectionLabels = useMemo<Record<SectionId, string>>(() => {
    const acc = {} as Record<SectionId, string>;
    sectionNav.forEach((item) => {
      acc[item.id] = item.label;
    });
    return acc;
  }, [sectionNav]);

  // Use ref to prevent infinite loops
  const hasRedirectedRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const hasAttemptedRef = useRef(false);
  /** بعد حذف الطلب: لا تُعاد اختيار صف تلقائياً حتى يغادر المستخدم خطوة بيانات الأرض */
  const skipRequestAutoSelectRef = useRef(false);

  /* --------------------------- Auth guard & Load data --------------------------- */
  useEffect(() => {
    // Prevent infinite loops - if already redirected, loaded, or attempted, don't run again
    if (hasRedirectedRef.current || hasLoadedRef.current || hasAttemptedRef.current) return;

    // Mark as attempted immediately to prevent re-running
    hasAttemptedRef.current = true;

    // Check authentication first - redirect immediately if not authenticated
    if (!isAuthenticated()) {
      hasRedirectedRef.current = true;
      setLoading(false);
      setRedirecting(true);
      // Build continue URL to return here after login
      const continuePath = `/benefaction/request/${landId}`;
      const continueToken = encodeURIComponent(continuePath);
      // Navigate immediately - don't wait
      navigate(`/benefaction/login?c=${continueToken}`, { replace: true });
      return;
    }

    // Validate landId
    if (!landId || landId <= 0) {
      setError("رقم الأرض غير صحيح");
      setLoading(false);
      setTimeout(() => navigate("/benefaction/lands"), 2000);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch land data (this doesn't require auth)
        const ld = (await donationsService.getDonationLandById(landId)) as LandDetail;
        if (!mounted) return;
        if (!ld) {
          setError("الأرض غير موجودة");
          setLoading(false);
          setTimeout(() => navigate("/benefaction/lands"), 2000);
          return;
        }
        setLand(ld);

        // Fetch donors (this requires auth)
        const ds = (await donorService.listMine()) as DonorRow[];
        if (!mounted) return;
        setDonors(ds);
        if (ds.length > 0) {
          // URL donor token must win.
          // This is required for indirect donor requests opened from My Requests page.
          const donorFromUrl = donorTokenFromUrl
            ? ds.find((d) => d.donorIdToken === donorTokenFromUrl)
            : null;

          const ownerDonor = donorService.pickDonorForCurrentUser(ds);

          let initialDonorToken = donorFromUrl?.donorIdToken;
          if (!initialDonorToken) {
            try {
              const stored = sessionStorage.getItem(landSelectedDonorStorageKey(landId));
              if (stored && ds.some((d) => d.donorIdToken === stored)) {
                initialDonorToken = stored;
              }
            } catch {
              /* ignore */
            }
          }
          setSelectedDonorToken(initialDonorToken ?? ownerDonor?.donorIdToken ?? ds[0].donorIdToken);
        } else {
          setError("لا يوجد متبرعون. يرجى إضافة متبرع أولاً");
          setShowNotBenefactorModal(true);
        }

        // Mark as loaded only after successful load
        hasLoadedRef.current = true;
      } catch (err: any) {
        if (!mounted) return;

        // Handle AUTH_401 immediately - redirect to login (check all possible variations)
        const errorMessage = err?.message || err?.toString() || "";
        const errorCode = err?.code || "";
        const isAuthError =
          errorMessage === "AUTH_401" ||
          errorCode === "AUTH_401" ||
          errorMessage.includes("AUTH_401") ||
          errorMessage.includes("401") ||
          err?.response?.status === 401;

        if (isAuthError) {
          hasRedirectedRef.current = true;
          setLoading(false);
          setRedirecting(true);
          setError(null); // Clear any error before redirect
          const continuePath = `/benefaction/request/${landId}`;
          const continueToken = encodeURIComponent(continuePath);
          // Redirect immediately without showing error
          navigate(`/benefaction/login?c=${continueToken}`, { replace: true });
          return;
        }

        // Handle timeout errors
        if (
          errorMessage === "REQUEST_TIMEOUT" ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("Timeout")
        ) {
          setError("انتهت مهلة الاتصال. يرجى التحقق من الاتصال بالخادم والمحاولة مرة أخرى");
          // Mark as attempted to prevent infinite retry
          hasLoadedRef.current = true;
          return;
        }

        // Handle network errors
        if (
          errorMessage === "NETWORK_ERROR" ||
          errorMessage.includes("network") ||
          errorMessage.includes("Network")
        ) {
          setError("فشل الاتصال بالخادم. يرجى التحقق من الاتصال والمحاولة مرة أخرى");
          // Mark as attempted to prevent infinite retry
          hasLoadedRef.current = true;
          return;
        }

        // For other errors, show error message
        const errorMsg = errorMessage || "حدث خطأ أثناء تحميل البيانات";
        console.error("Error loading donation request page:", err);
        setError(errorMsg);
        // Mark as attempted to prevent infinite retry
        hasLoadedRef.current = true;
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landId]);

  useEffect(() => {
    if (!landId || !selectedDonorToken) return;
    try {
      sessionStorage.setItem(landSelectedDonorStorageKey(landId), selectedDonorToken);
    } catch {
      /* ignore */
    }
  }, [landId, selectedDonorToken]);

  /* -------------------------------- Helpers -------------------------------- */

  const mapEmbedSrc = useMemo(() => {
    if (!land?.latitude || !land?.longitude) return "";
    return `https://www.google.com/maps?q=${land.latitude},${land.longitude}&hl=ar&z=16&output=embed`;
  }, [land]);

  const mapsDeeplink = useMemo(() => {
    if (!land?.latitude || !land?.longitude) return "";
    return `https://maps.google.com/?q=${land.latitude},${land.longitude}&hl=ar`;
  }, [land]);

  const qrImgSrc = useMemo(() => {
    if (!mapsDeeplink) return "";
    return `https://quickchart.io/qr?text=${encodeURIComponent(mapsDeeplink)}&margin=2&size=160`;
  }, [mapsDeeplink]);

  const landPrice: number = useMemo(() => {
    const p = land?.aproxLandPrice ?? land?.approxLandPrice ?? (land as any)?.aprox_land_price;
    return typeof p === "number" ? p : 0;
  }, [land]);

  const landLabel = useMemo(
    () => (land ? land.code || land.landCode || land.sequence || `#${land.id}` : ""),
    [land]
  );

  const fullAddr = useMemo(
    () =>
      land
        ? land.fullAddress ||
          [land.regionName, land.cityName, land.centerName, land.districtName].filter(Boolean).join(" / ")
        : "",
    [land]
  );

  const landInfoRows: LandInfoRows | null = useMemo(
    () => (land ? buildLandInfoRows(land as unknown as Record<string, unknown>, landLabel, fullAddr) : null),
    [land, landLabel, fullAddr]
  );

  const donationTypeId = 1;

  const maskNin = (nin?: string) => {
    const d = (nin || "").replace(/\D+/g, "");
    if (d.length < 6) return d;
    const head = d.slice(0, 2);
    const tail = d.slice(-4);
    return `${head}${"X".repeat(d.length - 6)}${tail}`;
  };
  const onlyDigits = (s: string | undefined | null) => (s ?? "").replace(/\D+/g, "");
  const LRM = "\u200E",
    RLM = "\u200F",
    LRI = "\u2066",
    PDI = "\u2069";
  const donorLabel = (name: string, nin?: string) => `${RLM}${name}${RLM} ${LRM}–${LRM} ${LRI}${maskNin(nin)}${PDI}`;

  const docTypeLabel = (v: number) =>
    v === DOC.FinancialCapacity ? t("Docs:financialCapacity", "خطاب الملاءة المالية") : t("Docs:poaImage", "صك التوكيل الشرعي");

  const handleSectionSelect = (id: SectionId) => {
    setActiveSection(id);
    setMobileNavOpen(false);
  };

  async function apiPost<T>(path: string, body: any): Promise<T> {
    const tok = getAuthToken?.();
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    return res.json();
  }
  function stripDataUrl(s: string) {
    const i = s.indexOf("base64,");
    return i >= 0 ? s.substring(i + 7) : s;
  }
  function fileToBase64(file: File) {
    return new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ""));
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
  }

  /* --------------------------- Requests: list/select --------------------------- */

  function buildSubmitPayload() {
    if (!selectedReq) return null;
    const userLang = "ar_001"; // Hardcoded for Arabic
    return {
      requestToken: selectedReq.tokenReqId,
      donationTypeId: selectedReq.donationTypeId ?? donationTypeId,
      landId: selectedReq.landId ?? land?.id ?? 0,
      requestedAmount: selectedReq.requestedAmount ?? landPrice,
      finalAmount: selectedReq.finalAmount ?? landPrice,
      userLang,
      donorToken: selectedDonorToken,
    };
  }

  async function refreshRequests(prefer?: { code?: string }): Promise<DonationRequestItem[]> {
    if (!selectedDonorToken) {
      setRequests([]);
      setSelectedReq(null);
      setFiles([]);
      return [];
    }
    try {
      setLoadingRequests(true);
      const rows = await listDonationRequests(selectedDonorToken, land?.id);
      setRequests(rows);

      if (skipRequestAutoSelectRef.current) {
        setSelectedReq(null);
        return rows;
      }

      let pick: DonationRequestItem | null = null;
      if (reqTokenFromUrl) {
        pick = rows.find((r) => r.tokenReqId === reqTokenFromUrl) ?? null;
      }

      if (!pick && prefer?.code) {
        pick = rows.find((r) => r.code === prefer.code) ?? null;
      }

      if (!pick && land?.id) {
        pick = rows.find((r) => r.landId === land.id) ?? null;
      }

      if (!pick) {
        pick = rows[0] ?? null;
      }
      setSelectedReq(pick);
      return rows;
    } finally {
      setLoadingRequests(false);
    }
  }

  useEffect(() => {
    if (!selectedDonorToken) {
      setRequests([]);
      setSelectedReq(null);
      setFiles([]);
      return;
    }
    refreshRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDonorToken, land?.id, reqTokenFromUrl]);

  useEffect(() => {
    if (activeSection !== "land") {
      skipRequestAutoSelectRef.current = false;
    }
  }, [activeSection]);

  /* --------------------------- Files: list, download, preview --------------------------- */
  async function fetchFilesForSelected() {
    if (!selectedReq?.tokenReqId) {
      setFiles([]);
      setPreviews({});
      return;
    }
    try {
      setFilesLoading(true);
      const payload: any = { requestToken: selectedReq.tokenReqId };
      const data = await apiPost<RequestFileRow[]>("/api/donation/request/get/files", payload);

      const map = new Map<number | string, RequestFileRow>();
      (Array.isArray(data) ? data : []).forEach((r) => {
        const key = r?.id ?? r?.attachmentId ?? `${r.name}-${r.documentTypeId}`;
        map.set(key, r);
      });
      setFiles([...map.values()]);
    } finally {
      setFilesLoading(false);
    }
  }

  useEffect(() => {
    if (!selectedReq?.tokenReqId) {
      setFiles([]);
      setPreviews({});
      return;
    }
    setUploadErr(null);
    setPendingFiles({
      [DOC.FinancialCapacity]: null,
      [DOC.PowerOfAttorney]: null,
    });
    fetchFilesForSelected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReq?.tokenReqId]);

  async function downloadByDonor(f: RequestFileRow) {
    const tok = getAuthToken?.();
    const qs = new URLSearchParams({
      attachmentId: String(f.attachmentId),
      accessToken: f.accessToken,
      tokenDonorId: f.tokenDonorId,
    });
    const res = await fetch(`${API_BASE_URL}/api/donation/file/downloadByDonor?${qs.toString()}`, {
      method: "GET",
      headers: { Accept: "*/*", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
    });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();

    const cd = res.headers.get("content-disposition") || "";

    const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
    const filename = m ? decodeURIComponent(m[1]) : f.name?.trim() || docTypeLabel(f.documentTypeId);

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    let closed = false;
    async function load() {
      if (!selectedReq?.tokenReqId) {
        setPreviews({});
        return;
      }
      const next: Record<number, string> = {};
      for (const f of files) {
        try {
          const tok = getAuthToken?.();
          const qs = new URLSearchParams({
            attachmentId: String(f.attachmentId),
            accessToken: f.accessToken,
            tokenDonorId: f.tokenDonorId,
          });
          const res = await fetch(`${API_BASE_URL}/api/donation/file/downloadByDonor?${qs.toString()}`, {
            method: "GET",
            headers: { Accept: "*/*", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
          });
          if (!res.ok) continue;
          const contentType = res.headers.get("content-type") || "";
          if (!contentType.startsWith("image/")) continue;
          const blob = await res.blob();
          if (closed) return;
          next[f.id] = URL.createObjectURL(blob);
        } catch {
          /* ignore */
        }
      }
      if (!closed) setPreviews(next);
    }
    load();
    return () => {
      closed = true;
      Object.values(previews).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, selectedReq?.tokenReqId]);

  /* ------------------------------ Upload & delete ------------------------------ */

  async function doUpload(file: File, type: number) {
    if (!selectedReq) return;
    if (files.some((f) => f.documentTypeId === type)) {
      setUploadErr(t("uploads.already", "تم رفع هذا النوع من المستندات مسبقًا"));
      return;
    }
    if (!ACCEPT_MIME.includes(file.type) || file.size > MAX_BYTES) {
      setUploadErr(
        t("uploads.invalidTypeOrSize", "يرجى تحميل ملف بصيغة مسموح بها ولا يتجاوز حجمه {{max}} ميجابايت", {
          max: MAX_MB,
        })
      );
      return;
    }
    const dataUrl = await fileToBase64(file);
    await apiPost("/api/donation/request/fileUpload", {
      requestToken: selectedReq.tokenReqId,
      fileName: file.name,
      documentType: type,
      description: "",
      fileBase64: stripDataUrl(dataUrl),
    });
    await fetchFilesForSelected();
  }

  function setPendingFile(type: number, file: File | null) {
    if (file) {
      if (!ACCEPT_MIME.includes(file.type) || file.size > MAX_BYTES) {
        setUploadErr(
          t("uploads.invalidTypeOrSize", "يرجى تحميل ملف بصيغة مسموح بها ولا يتجاوز حجمه {{max}} ميجابايت", {
            max: MAX_MB,
          })
        );
        return;
      }
      if (files.some((f) => f.documentTypeId === type)) {
        setUploadErr(t("uploads.already", "تم رفع هذا النوع من المستندات مسبقًا"));
        return;
      }
    }
    setUploadErr(null);
    setPendingFiles((prev) => ({ ...prev, [type]: file }));
  }

  async function uploadPendingIfNeeded() {
    if (!selectedReq) return;

    const hasFinancial = files.some((f) => f.documentTypeId === DOC.FinancialCapacity);
    const hasPoA = files.some((f) => f.documentTypeId === DOC.PowerOfAttorney);

    if (!hasFinancial) {
      const f = pendingFiles[DOC.FinancialCapacity];
      if (!f) throw new Error("MISSING_FINANCIAL");
      await doUpload(f, DOC.FinancialCapacity);
      setPendingFiles((p) => ({ ...p, [DOC.FinancialCapacity]: null }));
    }

    if (!isDirectDonor && !hasPoA) {
      const f = pendingFiles[DOC.PowerOfAttorney];
      if (!f) throw new Error("MISSING_POA");
      await doUpload(f, DOC.PowerOfAttorney);
      setPendingFiles((p) => ({ ...p, [DOC.PowerOfAttorney]: null }));
    }
  }

  async function deleteSelectedRequest() {
    if (!selectedReq) return;
    if (!window.confirm(t("confirmDelete", "هل أنت متأكد من حذف الطلب؟"))) return;
    try {
      await apiPost("/api/donation/request/delete", { requestToken: selectedReq.tokenReqId });
    } catch (e) {
      console.error(e);
      setError(t("deleteFailed", "تعذر حذف الطلب. يرجى المحاولة مرة أخرى"));
      return;
    }

    setError(null);
    skipRequestAutoSelectRef.current = true;

    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.delete("req");
      return next;
    });

    setCreated(null);
    setActiveSection("land");
    setMobileNavOpen(false);
    setSubmitErr(null);
    setSubmitOk(null);
    setDeclarationAccepted(false);
    setFiles([]);
    setPreviews({});
    setPendingFiles({
      [DOC.FinancialCapacity]: null,
      [DOC.PowerOfAttorney]: null,
    });

    await refreshRequests();
  }

  /* --------------------------------- Create flow --------------------------------- */

  async function onStartRequest() {
    if (!land || !selectedDonorToken) {
      setError(t("noDonors", "لا يوجد متبرعون. يرجى إضافة متبرع أولاً"));
      setShowNotBenefactorModal(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    setCreated(null);
    const selectedDonor = donors.find((d) => d.donorIdToken === selectedDonorToken);
    try {
      const payload = {
        nationalId: selectedDonor?.nationalId ? onlyDigits(selectedDonor.nationalId) : null,
        donorIdToken: selectedDonorToken,
        donationTypeId,
        landId: land.id,
        requestedAmount: landPrice,
        finalAmount: landPrice,
      };
      const result = await createDonationRequest(payload);
      const rows = await refreshRequests({ code: result.code });
      const codeFromList = result.code || rows.find((r) => r.id === result.id)?.code || null;
      setCreated({ ...result, code: codeFromList || result.code });
      // Put the newly created request token into the URL (no local memory)
      const picked = rows.find((r) => r.code === (codeFromList || result.code)) ?? null;
      if (picked?.tokenReqId) {
        setSearchParams((p) => {
          const next = new URLSearchParams(p);
          next.set("req", picked.tokenReqId);
          return next;
        });
      }
    } catch (err: any) {
      const apiMessage =
        err?.message ||
        t("createError", "تعذر إنشاء الطلب. يرجى المحاولة مرة أخرى");

      setError(apiMessage);
      setCreated(null);
      return;
    } finally {
      setSubmitting(false);
    }
  }

  /* ------------------------------- Submit (finalize) ------------------------------- */

  const selectedDonor = useMemo(() => donors.find((d) => d.donorIdToken === selectedDonorToken) || null, [donors, selectedDonorToken]);
  const donorType = (selectedDonor?.donorType || "direct").toLowerCase();
  const isDirectDonor = donorType === "direct";
  const hasFinancial = files.some((f) => f.documentTypeId === DOC.FinancialCapacity);
  const hasPoA = files.some((f) => f.documentTypeId === DOC.PowerOfAttorney);
  const hasFinancialEffective = hasFinancial || !!pendingFiles[DOC.FinancialCapacity];
  const hasPoAEffective = hasPoA || !!pendingFiles[DOC.PowerOfAttorney];

  const isDraft = (selectedReq?.state || "").toLowerCase() === "draft" || (selectedReq?.stageName || "").toLowerCase() === "draft";

  const requirementsMet =
    isDraft && hasFinancialEffective && (isDirectDonor || (!isDirectDonor && hasPoAEffective));

  useEffect(() => {
    setDeclarationAccepted(false);
  }, [activeSection, selectedReq?.tokenReqId]);

  async function onSubmitRequestInline() {
    if (!selectedReq) return;
    setSubmitErr(null);
    setSubmitOk(null);
    try {
      setSubmittingReq(true);
      // Upload local picks (pending) before submit
      try {
        await uploadPendingIfNeeded();
      } catch (e: any) {
        const code = String(e?.message || "");
        if (code === "MISSING_FINANCIAL") {
          setSubmitErr(t("uploads.requiredFinancial", "يرجى اختيار خطاب الملاءة المالية قبل إرسال الطلب"));
          return;
        }
        if (code === "MISSING_POA") {
          setSubmitErr(t("uploads.requiredPoa", "يرجى اختيار صك التوكيل الشرعي قبل إرسال الطلب"));
          return;
        }
        setSubmitErr(t("uploads.uploadFailed", "فشل رفع الملف. يرجى المحاولة مرة أخرى"));
        return;
      }
      const payload = buildSubmitPayload();
      if (!payload) return;
      await apiPost("/api/donation/request/submit", payload);
      setSubmitOk(t("declare.success", "تم إرسال الطلب بنجاح"));
      await refreshRequests();
      // البقاء على مرحلة «إرسال الطلب» بعد التقديم الناجح
      setActiveSection("send");
    } catch (e) {
      console.error("Submit failed:", e);
      setSubmitErr(t("submitError", "حدث خطأ أثناء إرسال الطلب"));
    } finally {
      setSubmittingReq(false);
    }
  }

  /* --------------------------------- Render --------------------------------- */

  // Empty/loading states now use the same container width via dga-container
  if (redirecting) {
    return (
      <div className="dga-container py-16" dir={dir}>
        <div
          className="mx-auto max-w-md rounded-3xl border border-gray-200 bg-white px-8 py-10 text-center shadow-lg shadow-gray-200/60"
          role="status"
          aria-live="polite"
        >
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-sa-50 text-sa-600">
            <svg className="h-7 w-7 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-90"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <p className="text-base font-medium text-neutral-800">جاري التحويل إلى صفحة تسجيل الدخول…</p>
          <p className="mt-2 text-sm text-neutral-500">سيتم فتح صفحة الدخول تلقائيًا.</p>
        </div>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="dga-container py-10" dir={dir}>
        <div className="mx-auto max-w-3xl" role="status" aria-live="polite" aria-busy="true">
          <div className="mb-6 space-y-3">
            <div className="h-8 w-48 animate-pulse rounded-lg bg-neutral-200/80" />
            <div className="h-4 w-72 max-w-full animate-pulse rounded bg-neutral-100" />
          </div>
          <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl">
            <div className="h-24 animate-pulse bg-gradient-to-l from-slate-50 to-white" />
            <div className="space-y-4 p-6">
              <div className="h-4 w-full animate-pulse rounded bg-neutral-100" />
              <div className="h-4 w-11/12 animate-pulse rounded bg-neutral-100" />
              <div className="h-4 w-4/5 animate-pulse rounded bg-neutral-100" />
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="h-24 animate-pulse rounded-2xl bg-neutral-50" />
                <div className="h-24 animate-pulse rounded-2xl bg-neutral-50" />
              </div>
            </div>
          </div>
          <p className="mt-6 text-center text-sm text-neutral-500">{t("loading", "جاري التحميل...")}</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="dga-container py-10" dir={dir}>
        <div className="mx-auto max-w-lg overflow-hidden rounded-3xl border border-red-200 bg-red-50/90 shadow-lg shadow-red-100/40">
          <div className="flex gap-4 px-5 py-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-100 text-red-700" aria-hidden>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-sm font-semibold text-red-900">{t("errorTitle", "تعذر المتابعة")}</p>
              <p className="mt-1 text-sm leading-relaxed text-red-800">{error}</p>
            </div>
          </div>
          <div className="border-t border-red-200/80 bg-white/60 px-5 py-4">
            <button
              type="button"
              className="inline-flex w-full items-center justify-center rounded-xl border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-800 shadow-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500 sm:w-auto"
              onClick={() => navigate(-1)}
            >
              {t("back", "رجوع")}
            </button>
          </div>
        </div>
        <NotBenefactorModal open={showNotBenefactorModal} onClose={() => setShowNotBenefactorModal(false)} isLoggedIn={true} />
      </div>
    );
  }
  if (!land) {
    return (
      <div className="dga-container py-10" dir={dir}>
        <div className="mx-auto max-w-lg overflow-hidden rounded-3xl border border-amber-200 bg-amber-50/90 shadow-lg shadow-amber-100/40">
          <div className="flex gap-4 px-5 py-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-800" aria-hidden>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-sm font-semibold text-amber-950">لم يتم العثور على بيانات الأرض</p>
              <p className="mt-1 text-sm text-amber-900/90">قد يكون الرابط غير صحيح أو تم نقل الأرض.</p>
            </div>
          </div>
          <div className="border-t border-amber-200/80 bg-white/60 px-5 py-4">
            <button
              type="button"
              className="inline-flex w-full items-center justify-center rounded-xl border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-800 shadow-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500 sm:w-auto"
              onClick={() => navigate("/benefaction/lands")}
            >
              {t("browseLands", "تصفح الأراضي المتاحة")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen" dir={dir}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="mb-6">
          <Breadcrumbs />
        </div>

        <div className="rounded-3xl border border-gray-200 bg-white/90 shadow-xl overflow-hidden">
          <div className="px-4 md:px-6 py-6 border-b border-gray-100 bg-white/75 backdrop-blur">
            <div className="flex items-center gap-4">
              <div className="min-w-[96px]" aria-hidden />
              <div className="flex-1" aria-hidden />
              <div className="flex items-center justify-end min-w-[96px]">
                <button
                  className="inline-flex items-center justify-center rounded-xl border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
                  onClick={() => navigate(-1)}
                >
                  {t("back", "رجوع")}
                </button>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 py-4 border-b border-gray-100 bg-slate-50/60">
            <LandDonationJourneyStepper
              landLoaded={!!land}
              activeSection={activeSection}
              hasRequestRow={requests.length > 0}
              isDraft={isDraft}
              requirementsMet={requirementsMet}
              onSelectSection={handleSectionSelect}
            />
          </div>

          <div className="px-4 md:px-6 py-6">
            <div className="flex flex-col xl:flex-row gap-6">
              <aside
                className={`flex-shrink-0 transition-[width] duration-200 ease-out xl:w-72 ${
                  sectionSidebarCollapsed ? "xl:!w-[4.25rem]" : ""
                }`}
              >
                <nav
                  id="request-sections-nav"
                  className={`hidden xl:block sticky top-28 bg-white/80 border border-gray-200 rounded-2xl shadow-sm space-y-2 transition-[padding] duration-200 ${
                    sectionSidebarCollapsed ? "p-2" : "p-3"
                  }`}
                  aria-label={t("nav.menuTitle", "أقسام الطلب")}
                >
                  <button
                    type="button"
                    onClick={() => setSectionSidebarCollapsed((c) => !c)}
                    className="hidden xl:flex w-full items-center justify-center rounded-xl border border-gray-200 bg-gray-50/90 py-2 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-sa-500"
                    aria-expanded={!sectionSidebarCollapsed}
                    aria-controls="request-sections-nav-items"
                    title={
                      sectionSidebarCollapsed
                        ? t("nav.expandSections", "إظهار أقسام الطلب")
                        : t("nav.collapseSections", "طي أقسام الطلب")
                    }
                  >
                    {sectionSidebarCollapsed ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M6 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                  <div id="request-sections-nav-items" className="space-y-2">
                    {sectionNav.map((section) => {
                      const isActive = activeSection === section.id;
                      return (
                        <button
                          key={section.id}
                          type="button"
                          onClick={() => handleSectionSelect(section.id)}
                          title={sectionSidebarCollapsed ? section.label : undefined}
                          aria-current={isActive ? "true" : "false"}
                          aria-label={section.label}
                          className={`w-full flex items-center gap-2 font-semibold rounded-xl transition-all duration-200 ${
                            sectionSidebarCollapsed
                              ? `justify-center px-2 py-3 text-sm ${isActive ? "bg-sa-100 text-sa-700 shadow-inner border border-sa-200" : "text-gray-700 hover:bg-gray-100 border border-transparent"}`
                              : `text-right text-sm px-4 py-3 ${
                                  isActive
                                    ? "bg-sa-100 text-sa-700 shadow-inner border border-sa-200"
                                    : "text-gray-700 hover:bg-gray-100 border border-transparent"
                                }`
                          }`}
                        >
                          {sectionSidebarCollapsed ? (
                            <SectionNavGlyph id={section.id} />
                          ) : (
                            section.label
                          )}
                        </button>
                      );
                    })}
                  </div>
                </nav>

                <div className="xl:hidden bg-white/80 border border-gray-200 rounded-2xl shadow-sm">
                  <button
                    type="button"
                    onClick={() => setMobileNavOpen((prev) => !prev)}
                    className="w-full flex items-center justify-between px-4 py-3 text-right text-sm font-semibold text-gray-700"
                    aria-expanded={mobileNavOpen}
                  >
                    <span>{sectionLabels[activeSection]}</span>
                    <svg
                      className={`w-5 h-5 transition-transform duration-200 ${mobileNavOpen ? "rotate-180" : ""}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {mobileNavOpen && (
                    <div className="border-t border-gray-200">
                      {sectionNav.map((section) => {
                        const isActive = activeSection === section.id;
                        return (
                          <button
                            key={section.id}
                            type="button"
                            onClick={() => handleSectionSelect(section.id)}
                            className={`w-full text-right text-sm font-semibold px-4 py-3 transition-all duration-200 ${
                              isActive ? "text-sa-700 bg-sa-50" : "text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            {section.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </aside>

              <div className="flex-1">
                {activeSection === "land" ? (
                  <LandInfoPanel
                    aria-labelledby="land-info-summary"
                    t={t}
                    mapEmbedSrc={mapEmbedSrc}
                    qrImgSrc={qrImgSrc}
                    mapsDeeplink={mapsDeeplink}
                    landLabel={landLabel}
                    landInfo={landInfoRows}
                    landPrice={landPrice}
                    numberSar={numberSar}
                    onNext={() => handleSectionSelect("applicant")}
                  />
                ) : (
                  <div className="space-y-6">
                    {activeSection === "applicant" && (
                      <ApplicantPanel
                        t={t}
                        donors={donors}
                        donorLabel={donorLabel}
                        selectedDonor={selectedDonor}
                        selectedDonorToken={selectedDonorToken}
                        setSelectedDonorToken={setSelectedDonorToken}
                        submitting={submitting}
                        landPrice={landPrice}
                        numberSar={numberSar}
                        created={created}
                        error={error}
                        onStartRequest={onStartRequest}
                        onGoToDocuments={() => {
                          setActiveSection("requests");
                          setMobileNavOpen(false);
                        }}
                        onOpenBenefactorList={() => navigate("/benefaction/benefactors")}
                      />
                    )}

                    {(activeSection === "requests" || activeSection === "send") && (
                      <RequestsSection
                        t={t}
                        requests={requests}
                        selectedReq={selectedReq}
                        onSelect={(r) => setSelectedReq(r)}
                        loadingRequests={loadingRequests}
                        numberSar={numberSar}
                        stageLabel={labelForLandRequestStage}
                        hideRequestsTable={true}
                        isDraft={isDraft}
                        isDirectDonor={isDirectDonor}
                        hasFinancial={hasFinancialEffective}
                        hasPoA={hasPoAEffective}
                        requirementsMet={requirementsMet}
                        confirmChecked={declarationAccepted}
                        onConfirmChange={setDeclarationAccepted}
                        onSubmitRequest={onSubmitRequestInline}
                        submittingReq={submittingReq}
                        onDelete={deleteSelectedRequest}
                        docType={docType}
                        setDocType={setDocType}
                        uploadBusy={uploadBusy}
                        uploadErr={uploadErr}
                        setUploadErr={setUploadErr}
                        MAX_MB={MAX_MB}
                        pendingFiles={pendingFiles}
                        setPendingFile={setPendingFile}
                        filesLoading={filesLoading}
                        files={files}
                        previews={previews}
                        downloadByDonor={downloadByDonor}
                        submitErr={submitErr}
                        submitOk={submitOk}
                        onGoToDashboard={() => navigate("/my-dashboard")}
                        onGoToMyRequests={() => navigate(`/my-requests?donor=${encodeURIComponent(selectedDonorToken)}&tab=lands`)}
                        mode={activeSection === "send" ? "send" : "documents"}
                        onNext={() => setActiveSection("send")}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
